/* eslint-disable @typescript-eslint/no-explicit-any -- redux-persist transforms operate on opaque serialized state */
import { createMigrate, createTransform, PersistedState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { scrubBlobUrl } from '../reducers/vehiclePhotosSlice';

// Transform to handle Date objects
const dateTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState: any) => {
    const processValue = (value: any): any => {
      if (value instanceof Date) {
        return {
          __type: 'Date',
          value: value.toISOString()
        };
      }
      if (Array.isArray(value)) {
        return value.map(processValue);
      }
      if (value !== null && typeof value === 'object') {
        const processed: any = {};
        for (const key in value) {
          processed[key] = processValue(value[key]);
        }
        return processed;
      }
      return value;
    };
    return processValue(inboundState);
  },
  // Transform state being rehydrated
  (outboundState: any) => {
    const processValue = (value: any): any => {
      if (value && typeof value === 'object' && value.__type === 'Date') {
        return new Date(value.value);
      }
      if (Array.isArray(value)) {
        return value.map(processValue);
      }
      if (value !== null && typeof value === 'object' && !value.__type) {
        const processed: any = {};
        for (const key in value) {
          processed[key] = processValue(value[key]);
        }
        return processed;
      }
      return value;
    };
    return processValue(outboundState);
  }
);

type PersistedPhotoMap = Record<string, {
  type?: number;
  url?: string;
  uploadStatus?: string;
  filename?: string;
  uri?: string;
  photoId?: string;
}>;

/** Demote any photo without a scrubbed blob URL to failed (covers completed/pending/uploading). */
export function demotePhotoMap(
  photoMap: PersistedPhotoMap | undefined
): PersistedPhotoMap | undefined {
  if (!photoMap) return photoMap;
  const next: PersistedPhotoMap = {};
  for (const key of Object.keys(photoMap)) {
    const photo = photoMap[key];
    if (!photo) continue;
    const url = scrubBlobUrl(photo.url);
    next[key] = url
      ? {
          type: photo.type,
          url,
          uploadStatus: photo.uploadStatus === 'failed' ? 'failed' : 'completed',
          ...(photo.filename ? { filename: photo.filename } : {}),
        }
      : { type: photo.type, uploadStatus: 'failed' };
  }
  return next;
}

const photoTransform = createTransform(
  (inboundState: any) => {
    if (!inboundState?.photoMap) return inboundState;

    const lightPhotoMap: PersistedPhotoMap = {};
    for (const key of Object.keys(inboundState.photoMap)) {
      const photo = inboundState.photoMap[key];
      if (!photo) continue;
      const url = scrubBlobUrl(photo.url);
      lightPhotoMap[key] = {
        type: photo.type,
        url,
        uploadStatus: url ? (photo.uploadStatus ?? 'completed') : 'failed',
        ...(photo.filename ? { filename: photo.filename } : {}),
      };
    }

    return {
      ...inboundState,
      photoMap: lightPhotoMap,
    };
  },
  (outboundState: any) => {
    if (!outboundState?.photoMap) return outboundState;
    return {
      ...outboundState,
      photoMap: demotePhotoMap(outboundState.photoMap),
    };
  },
  { whitelist: ['vehiclePhotos'] }
);

const migrate = createMigrate(
  {
    // v1 → v2: fix photo entries only; leave other keys untouched
    2: (state: PersistedState) => {
      if (!state || typeof state !== 'object') return state;
      const s = state as PersistedState & {
        vehiclePhotos?: { photoMap?: PersistedPhotoMap };
      };
      if (!s.vehiclePhotos?.photoMap) return state;
      return {
        ...s,
        vehiclePhotos: {
          ...s.vehiclePhotos,
          photoMap: demotePhotoMap(s.vehiclePhotos.photoMap),
        },
      };
    },
  },
  { debug: false }
);

// Main persistence configuration
export const persistConfig = {
  key: 'shzoom-uptime-root',
  storage,
  version: 2,
  whitelist: [
    'auth',           // Persist authentication state
    'vehicle',        // Persist vehicle information
    'vehiclePhotos',  // Persist photo metadata (scrubbed blob urls, not local images)
    'damageLocations', // Persist damage locations
    'damagedParts',   // Persist damaged parts
    'incidentDetails', // Persist incident details
    'operatorInfo',   // Persist operator information
    'history',        // Persist incident history
    'navigation'      // Persist navigation state
  ],
  transforms: [dateTransform, photoTransform],
  migrate,
  // Debounce writes to improve performance
  throttle: 1000,
  // Merge strategy for rehydration
  stateReconciler: (inboundState: any, originalState: any) => {
    // Deep merge strategy preserving new state structure
    const reconciled = { ...originalState };
    
    for (const key in inboundState) {
      if (inboundState[key] !== undefined) {
        reconciled[key] = inboundState[key];
      }
    }
    
    return reconciled;
  }
};

// Configuration for session-only data (uses sessionStorage)
export const sessionPersistConfig = {
  key: 'shzoom-uptime-session',
  storage: {
    getItem: (key: string) => sessionStorage.getItem(key),
    setItem: (key: string, value: string) => sessionStorage.setItem(key, value),
    removeItem: (key: string) => sessionStorage.removeItem(key)
  },
  whitelist: ['currentStep', 'navigationState'],
  throttle: 500
};

// Storage size monitoring
export const getStorageSize = (): { used: number; quota: number } => {
  let used = 0;
  let quota = 5 * 1024 * 1024; // Default 5MB quota
  
  try {
    // Calculate localStorage usage
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
    
    // Try to get actual quota if available
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        if (estimate.quota) {
          quota = estimate.quota;
        }
      });
    }
  } catch (error) {
    console.error('Error calculating storage size:', error);
  }
  
  return { used, quota };
};

// Auto cleanup old data when storage is nearly full
export const autoCleanupStorage = async (threshold = 0.9): Promise<boolean> => {
  const { used, quota } = getStorageSize();
  const usage = used / quota;
  
  if (usage > threshold) {
    try {
      // Clear old incident data first
      const keys = Object.keys(localStorage);
      const incidentKeys = keys.filter(key => key.includes('incident_history_'));
      
      // Sort by timestamp and remove oldest
      incidentKeys.sort().slice(0, Math.floor(incidentKeys.length / 2)).forEach(key => {
        localStorage.removeItem(key);
      });
      
      return true;
    } catch (error) {
      console.error('Error during auto cleanup:', error);
      return false;
    }
  }
  
  return false;
};

// Persistence gate configuration
export const persistGateConfig = {
  loading: null, // Will be replaced with a loading component
  persistor: null as any // Will be set by the store
};